import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadPathApi } from '../../api/uploadPath';
import {
  ADD_FRAMEWORK_SEQUENCES,
  KEEP_AS_PLAN_READY,
  PICK_A_FRAMEWORK,
  PICK_FRAMEWORK_BEFORE_MAP,
  PLACE_SCENES_HEADING,
  PLACE_SCENES_LEDE,
  PLACE_UNDER_SEQUENCE,
  USE_FRAMEWORK_AS_PLAN,
  USE_FRAMEWORK_AS_PLAN_HELP,
} from '../../utils/writerCopy';
import './PlotTrackImportMap.css';

/**
 * Manual map of imported Plot Grid columns under Framework sequence headers.
 * The writer places each scene. Nothing is best-fit.
 */
export default function PlotTrackImportMap({ storyId, onChanged }) {
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!storyId) {
      setView(null);
      return;
    }
    try {
      const next = await uploadPathApi.view(storyId);
      setView(next);
      setError(null);
    } catch {
      setView(null);
    }
  }, [storyId]);

  useEffect(() => { load(); }, [load]);

  if (!view?.has_import || !(view.scenes || []).length) return null;

  const sequences = view.sequences || [];
  const holding = view.unassigned_sequence_name || 'Not on a Framework sequence yet';
  const keepAsPlan = view.plan_mode === 'keep_as_plan';

  const place = async (scene) => {
    setBusy(true);
    setError(null);
    try {
      const picked = picks[scene.id];
      const sequenceId = picked === undefined ? (scene.sequence_id || null) : (picked || null);
      const next = await uploadPathApi.mapScene(storyId, {
        sceneId: scene.id,
        sequenceId,
      });
      setView(next);
      onChanged?.();
    } catch (err) {
      setError(err?.message || 'Could not update the scene list.');
    } finally {
      setBusy(false);
    }
  };

  const addHeaders = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await uploadPathApi.ensureHeaders(storyId);
      setView(next);
      onChanged?.();
    } catch (err) {
      setError(err?.message || 'Could not update the scene list.');
    } finally {
      setBusy(false);
    }
  };

  const useFramework = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await uploadPathApi.setPlanMode(storyId, 'framework_as_plan');
      setView(next);
    } catch (err) {
      setError(err?.message || 'Could not update the scene list.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pt-import" aria-label={PLACE_SCENES_HEADING}>
      <h2>{PLACE_SCENES_HEADING}</h2>
      <p>{PLACE_SCENES_LEDE}</p>
      {!view.has_framework && (
        <>
          <p>{PICK_FRAMEWORK_BEFORE_MAP}</p>
          <button type="button" onClick={() => navigate('/frameworks')}>
            {PICK_A_FRAMEWORK}
          </button>
        </>
      )}
      {view.has_framework && (
        <button type="button" onClick={addHeaders} disabled={busy}>
          {ADD_FRAMEWORK_SEQUENCES}
        </button>
      )}
      <ul className="pt-import__scenes">
        {(view.scenes || []).map((scene) => {
          const selected = picks[scene.id] !== undefined
            ? picks[scene.id]
            : (scene.sequence_id || '');
          return (
            <li key={scene.id} className="pt-import__scene">
              <p className="pt-import__name">{scene.name}</p>
              {scene.purpose ? <p className="pt-import__purpose">{scene.purpose}</p> : null}
              <p className="pt-import__where">
                {scene.unassigned ? holding : scene.sequence_name}
              </p>
              <label>
                <span>{PLACE_UNDER_SEQUENCE}</span>
                <select
                  value={selected}
                  onChange={(event) => setPicks((prev) => ({
                    ...prev,
                    [scene.id]: event.target.value,
                  }))}
                  disabled={!sequences.length || busy}
                >
                  <option value="">{holding}</option>
                  {sequences.map((sequence) => (
                    <option key={sequence.id} value={sequence.id}>{sequence.name}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => place(scene)}
                disabled={busy || !sequences.length}
              >
                {PLACE_UNDER_SEQUENCE}
              </button>
            </li>
          );
        })}
      </ul>
      {keepAsPlan && (
        <div className="pt-import__plan">
          <p>{KEEP_AS_PLAN_READY}</p>
          <button type="button" onClick={useFramework} disabled={busy}>
            {USE_FRAMEWORK_AS_PLAN}
          </button>
          <p>{USE_FRAMEWORK_AS_PLAN_HELP}</p>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
